import { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Sparkles, Bell } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { scrollToId } from "@/components/mobix/Navbar";
import Logo from "@/components/mobix/Logo";
import Footer from "@/components/mobix/Footer";
import FloatingChat from "@/components/mobix/FloatingChat";

const EASE = [0.22, 1, 0.36, 1];

const REVEAL = { opacity: 0, y: 30 };
const REVEAL_IN = { opacity: 1, y: 0 };
const VIEWPORT = { once: true, margin: "-60px" };
const TRANS = { duration: 0.7, ease: EASE };

function useScrollTop() {
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, []);
}

function TopBar() {
  const { lang, setLang, t } = useLang();
  return (
    <header className="sticky top-0 z-40 w-full bg-white/90 backdrop-blur-md border-b border-mobix-navy/10">
      <div className="max-w-[1800px] mx-auto px-6 sm:px-8 md:px-12 lg:px-20 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="catalog-logo" aria-label="MOBIX home">
          <Logo className="h-8 w-auto" />
        </Link>
        <div className="flex items-center gap-5">
          <Link to="/" data-testid="catalog-back-home" className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-mobix-navy hover:text-mobix-orange transition-colors">
            <ArrowLeft size={16} /> {t.products.backHome}
          </Link>
          <div className="flex items-center rounded-3xl bg-mobix-mauve/10 p-1">
            {["bs", "en"].map((code) => (
              <button
                key={code}
                onClick={() => setLang(code)}
                data-testid={`catalog-lang-${code}`}
                className={`px-3 py-1 rounded-3xl text-xs font-bold uppercase transition-colors ${lang === code ? "bg-mobix-navy text-white" : "text-mobix-mauve"}`}
              >
                {code}
              </button>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
}

function ComingSoon({ onNotify }) {
  const { t } = useLang();
  return (
    <motion.div
      initial={REVEAL}
      whileInView={REVEAL_IN}
      viewport={VIEWPORT}
      transition={TRANS}
      data-testid="coming-soon"
      className="relative overflow-hidden rounded-3xl border border-mobix-navy/10 bg-[#F9FAFB] px-6 py-20 sm:py-28 text-center"
    >
      {/* subtle decorative rings */}
      <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-mobix-orange/5" aria-hidden="true" />
      <div className="pointer-events-none absolute -left-20 -bottom-20 h-64 w-64 rounded-full bg-mobix-navy/[0.04]" aria-hidden="true" />

      <div className="relative z-10">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl bg-mobix-orange/10">
          <Sparkles className="text-mobix-orange" size={28} />
        </div>
        <p className="mt-8 text-xs font-bold tracking-[0.28em] uppercase text-mobix-orange">{t.products.comingSoonKicker}</p>
        <h2 className="mt-4 font-extrabold uppercase tracking-tight text-mobix-navy text-[clamp(1.75rem,4vw,3rem)] leading-[1.05] break-words">
          {t.products.comingSoon}
        </h2>
        <p className="mt-5 mx-auto max-w-md text-base sm:text-lg font-medium text-mobix-navy/60 leading-relaxed">
          {t.products.comingSoonDesc}
        </p>
        <button
          onClick={onNotify}
          data-testid="notify-button"
          className="group mt-9 inline-flex items-center gap-3 rounded-3xl bg-mobix-orange px-9 py-4 text-base font-bold text-white transition-all duration-300 hover:bg-[#d95f22] hover:-translate-y-0.5 shadow-[0_16px_32px_rgba(240,108,40,0.30)]"
        >
          {t.products.notify}
          <Bell size={18} className="transition-transform duration-300 group-hover:rotate-12" />
        </button>
      </div>
    </motion.div>
  );
}

export function CategoryPage() {
  const { t } = useLang();
  const { category } = useParams();
  const navigate = useNavigate();
  useScrollTop();

  const card = t.products.cards.find((c) => c.key === category);

  useEffect(() => {
    if (!card) navigate("/", { replace: true });
  }, [card, navigate]);

  if (!card) return null;

  const goContact = () => {
    navigate("/");
    setTimeout(() => scrollToId("contact"), 500);
  };

  return (
    <div className="App">
      <TopBar />
      <main className="bg-white">
        <section className="max-w-[1800px] mx-auto px-6 sm:px-8 md:px-12 lg:px-20 pt-16 lg:pt-20 pb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-mobix-orange hover:opacity-80 transition-opacity">
            <ArrowLeft size={16} /> {t.products.title}
          </Link>
          <p className="mt-8 text-xs font-bold tracking-[0.28em] uppercase text-mobix-orange">{t.products.title}</p>
          <h1 className="mt-4 font-extrabold uppercase tracking-tight text-mobix-navy text-[clamp(2rem,4.8vw,3.75rem)] leading-[1.02] break-words" data-testid="category-title">
            {card.name}<span className="text-mobix-orange">.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base sm:text-lg font-medium text-mobix-navy/70 leading-relaxed">{card.desc}</p>
          <span className="mt-8 block h-[3px] w-32 bg-mobix-orange" aria-hidden="true" />
        </section>

        <section className="max-w-[1800px] mx-auto px-6 sm:px-8 md:px-12 lg:px-20 pb-24 lg:pb-32">
          <ComingSoon onNotify={goContact} />
        </section>
      </main>
      <Footer />
      <FloatingChat />
    </div>
  );
}

export function AllProductsPage() {
  const { t } = useLang();
  const navigate = useNavigate();
  useScrollTop();

  const goContact = () => {
    navigate("/");
    setTimeout(() => scrollToId("contact"), 500);
  };

  return (
    <div className="App">
      <TopBar />
      <main className="bg-white">
        <section className="max-w-[1800px] mx-auto px-6 sm:px-8 md:px-12 lg:px-20 pt-16 lg:pt-20 pb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-mobix-orange hover:opacity-80 transition-opacity">
            <ArrowLeft size={16} /> {t.products.backHome}
          </Link>
          <p className="mt-8 text-xs font-bold tracking-[0.28em] uppercase text-mobix-orange">{t.products.title}</p>
          <h1 className="mt-4 font-extrabold uppercase tracking-tight text-mobix-navy text-[clamp(2rem,4.8vw,3.75rem)] leading-[1.02] break-words" data-testid="all-products-title">
            {t.products.allTitle}<span className="text-mobix-orange">.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base sm:text-lg font-medium text-mobix-navy/70 leading-relaxed">{t.products.subtitle}</p>
          <span className="mt-8 block h-[3px] w-32 bg-mobix-orange" aria-hidden="true" />
        </section>

        <div className="max-w-[1800px] mx-auto px-6 sm:px-8 md:px-12 lg:px-20 pb-24 lg:pb-32 pt-4">
          <ComingSoon onNotify={goContact} />
        </div>
      </main>
      <Footer />
      <FloatingChat />
    </div>
  );
}
